import { useState } from "react";
import { MessageCircle, Phone, Copy, Check, ExternalLink } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { WHATSAPP_NUMBER, WHATSAPP_DISPLAY } from "@/lib/whatsapp";

const DEFAULT_MESSAGE =
  "Hi, I'd like to inquire about medical treatment options in Kerala, India.";

export default function WhatsAppButton() {
  const [copied, setCopied] = useState(false);
  const text = encodeURIComponent(DEFAULT_MESSAGE);

  // Two paths – native app link covers desktop + mobile, tel: as fallback
  const appLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${text}`;
  const telLink = `tel:+${WHATSAPP_NUMBER}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`+${WHATSAPP_NUMBER}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* ignore */
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid="whatsapp-float-btn"
          aria-label="Chat on WhatsApp"
          className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#20BD5A] text-white flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={12}
        className="w-[290px] p-0 rounded-2xl border-slate-200 shadow-2xl overflow-hidden"
        data-testid="whatsapp-popover"
      >
        <div className="bg-[#03045e] text-white px-5 py-4">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-full bg-[#25D366] flex items-center justify-center">
              <MessageCircle className="w-4 h-4" />
            </div>
            <div>
              <div className="font-outfit text-sm font-medium">Chat with us</div>
              <div className="text-xs text-white/60">Typically replies within the hour</div>
            </div>
          </div>
        </div>
        <div className="p-4 space-y-2 bg-white">
          <a
            href={appLink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-open-app"
            className="flex items-center justify-between gap-3 rounded-xl bg-[#25D366] hover:bg-[#20BD5A] text-white px-4 py-3 text-sm font-medium transition-colors"
          >
            <span className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4" /> Open in WhatsApp
            </span>
            <ExternalLink className="w-3.5 h-3.5 opacity-80" />
          </a>
          <a
            href={telLink}
            data-testid="whatsapp-call"
            className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 hover:bg-slate-100 text-[#03045e] px-4 py-3 text-sm font-medium transition-colors border border-slate-200"
          >
            <span className="flex items-center gap-2">
              <Phone className="w-4 h-4" /> Call {WHATSAPP_DISPLAY}
            </span>
          </a>
          <button
            onClick={handleCopy}
            data-testid="whatsapp-copy"
            className="w-full flex items-center justify-center gap-2 rounded-xl text-xs text-slate-500 hover:text-[#03045e] py-2 transition-colors"
          >
            {copied ? (
              <>
                <Check className="w-3.5 h-3.5 text-green-600" /> Number copied
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5" /> Copy number
              </>
            )}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
